/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import _cloneDeep from 'lodash/cloneDeep';
import type {ShopperCustomersTypes, ShopperProductsTypes} from 'commerce-sdk-isomorphic';

import type {RootState} from 'coreStore/types';
import type {
	CustomerProductListItem,
	CustomerProductListState,
	CustomerProductListsState,
} from 'coreStore/data/customerProductLists/types';
import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import {promiseWithRetry} from 'coreUtils/utils';
import {handleAsyncError, isError} from '../../utils';
import {useCustomerMgr} from 'coreHooks';
import {useCategoryActions} from 'coreHooks/categories';
import {
	selectCustomerProductLists,
	receiveLists,
	receiveList,
	setListVisibility as setListVisibilityAction,
	createListItem as createListItemAction,
	updateListItem as updateListItemAction,
	removeListItem as removeListItemAction,
	resetCustomerProductLists,
} from '../../../store/data/customerProductLists';
import {EEventType} from 'coreClasses/events/IEvents';
import type ProductModel from 'coreClasses/product/ProductModel';

import type {
	ICustomerProductLists,
	ICustomerProductListItemInput,
	IGetListOptions,
	IWishlistDataLayerOptions,
	TCustomerProductListItemUpdate,
	TSavedGuestItem,
} from './IUseCustomerProductLists';

/**
 * This hook is designed to add customer product list capabilities
 * to your app, it leverages the Commerce API - Shopper Customer endpoints.
 * It uses a React context to store customer product list globally.
 *
 * By default, the Shopper Customer Product List API allows a shopper to
 * save multiple product lists. However, the PWA only use a single
 * product list, which is the wishlist. There is another hook useWishlist,
 * which is built on top of this hook to add wishlist specific logic.
 *
 * This Hook only works when your components are wrapped in CustomerProductListProvider.
 */
export default function useCustomerProductLists(): ICustomerProductLists {
	const {managerRepository} = useGeneralProvider();
	const api = managerRepository.getCommerceApi();
	const dispatch = useDispatch();
	const CustomerMgr = useCustomerMgr();
	const customer = CustomerMgr.getCurrentCustomer();
	const customerProductLists: CustomerProductListsState | null =
		useSelector((state: RootState) => selectCustomerProductLists(state)) || null;
	const categoryActions = useCategoryActions();

	const getLists = handleAsyncError(() => {
		return api.shopperCustomers.getCustomerProductLists({
			parameters: {
				customerId: customer.customerId,
			},
		});
	});

	const createList = handleAsyncError((name: string, type: string) => {
		return api.shopperCustomers.createCustomerProductList({
			body: {
				type,
				name,
			},
			parameters: {
				customerId: customer.customerId,
			},
		});
	});

	const getList = handleAsyncError((listId: string) => {
		return api.shopperCustomers.getCustomerProductList({
			parameters: {
				customerId: customer.customerId,
				listId,
			},
		});
	});

	const updateListVisibility = handleAsyncError((listId: string, isPublic: boolean) => {
		return api.shopperCustomers.updateCustomerProductList({
			body: {
				// Sent so the product_list afterPATCH hook receives the list id in the request document
				id: listId,
				public: isPublic,
			},
			parameters: {
				customerId: customer.customerId,
				listId,
			},
		});
	});

	const createListItem = handleAsyncError((listId: string, item: ICustomerProductListItemInput, isPublic: boolean) => {
		const {id, c_variationGroupId, quantity, customizationID} = item;
		const body: ShopperCustomersTypes.CustomerProductListItem = {
			productId: id,
			quantity,
			public: isPublic,
			priority: 1,
			type: 'product',
			c_variationGroupId: c_variationGroupId,
			c_customizationID: customizationID,
		};

		Object.keys(item).forEach((k) => {
			if (k.startsWith('c_')) {
				body[k] = item[k];
			}
		});

		const requestItemCreation = () => api.shopperCustomers
			.createCustomerProductListItem({
				body,
				parameters: {
					customerId: customer.customerId,
					listId
				}
			})
			.then((result) => {
				if (isError(result)) {
					throw new Error(result.detail);
				}

				return result;
			});

		// promiseWithRetry is untyped: it resolves with the created item; no logger data is needed
		return promiseWithRetry(requestItemCreation, undefined, null) as Promise<ShopperCustomersTypes.CustomerProductListItem>;
	});

	const updateListItem = handleAsyncError((listId: string, item: TCustomerProductListItemUpdate, isPublic: boolean) => {
		const {id, productId, c_variationGroupId, quantity} = item;

		return api.shopperCustomers.updateCustomerProductListItem({
			body: {
				id,
				productId,
				c_variationGroupId,
				quantity,
				public: isPublic,
				priority: 1,
			},
			parameters: {
				customerId: customer.customerId,
				listId: listId,
				itemId: item.id,
			},
		});
	});

	const removeListItem = handleAsyncError((listId: string, itemId: string) => {
		return api.shopperCustomers.deleteCustomerProductListItem(
			{
				parameters: {
					itemId,
					listId,
					customerId: customer.customerId,
				},
			},
			true,
		);
	});

	const self = useMemo<ICustomerProductLists>(() => {
		return {
			get isInitialized(): boolean {
				return !!customerProductLists && Object.keys(customerProductLists).length !== 0;
			},

			async reset(): Promise<void> {
				await dispatch(resetCustomerProductLists());
			},

			/**
			 * Get customer's product lists.
			 */
			getLists: async (): Promise<ShopperCustomersTypes.CustomerProductListResult> => {
				const lists = await getLists();
				await dispatch(receiveLists(lists));

				return lists;
			},

			/**
			 * Create a new product list.
			 * @param {string} name
			 * @param {string} type
			 */
			createList: async (name: string, type: string): Promise<CustomerProductListState> => {
				const list = await createList(name, type);
				await dispatch(receiveList(list));

				return list;
			},

			/**
			 * Get a specific product list by id.
			 * @param {string} listId
			 * @param {IGetListOptions} options
			 */
			getList: async (listId: string, options?: IGetListOptions): Promise<CustomerProductListState> => {
				const {detail} = options || {};
				let list: CustomerProductListState = await getList(listId);

				if (detail) {
					// automatically fetch details of the items in the list
					list = await self.getProductDetails(list);
				}

				await dispatch(receiveList(list));

				return list;
			},

			/**
			 * Get a product list for the registered user or
			 * creates a new list if none exists, due to the api
			 * limitation, we can not filter the lists based on
			 * name/type, therefore it fetches all lists.
			 * @param {string} name
			 * @param {string} type
			 * @param {IGetListOptions} options
			 * @param {string} listId
			 * @param {boolean} options.detail boolean flag to enable/disable fetching product details
			 * @param {TSavedGuestItem[]} savedGuestItems optional array of guest items to merge (when customerProductLists is already reset)
			 */
			getOrCreateList: async (
				name?: string,
				type?: string,
				options?: IGetListOptions,
				listId?: string,
				savedGuestItems?: TSavedGuestItem[],
			): Promise<CustomerProductListState> => {
				const isAgent = customer.isAgent();
				let previousProductListItems = [];

				// If savedGuestItems is provided, use it directly (for guest-to-registered merge after store reset)
				if (savedGuestItems) {
					previousProductListItems = savedGuestItems;
				} else if (listId) {
					const currentProductList = customerProductLists[listId];

					if (currentProductList?.customerProductListItems) {
						previousProductListItems = _cloneDeep(currentProductList.customerProductListItems);
					}

					await dispatch(resetCustomerProductLists());
				}

				const {detail} = options || {};
				let response = await getLists();
				// Note: if list is empty, the API response
				// does NOT contain the "data" key.
				let list: CustomerProductListState | undefined = response.data?.find((list) => {
					if (name) {
						return list.name === name;
					} else if (type) {
						return list.type === type;
					}

					return true;
				});

				if (!list) {
					list = await createList(name, type);
				}

				if (previousProductListItems.length > 0 && !isAgent) {
					for (let i = 0; i < previousProductListItems.length; i++) {
						let item = {
							id: previousProductListItems[i].productId,
							quantity: previousProductListItems[i].quantity,
							c_variationGroupId: previousProductListItems[i].c_variationGroupId,
						};

						await createListItem(list.id, item, !!list.public);
					}

					const updatedList = await getLists();
					list = updatedList.data?.find((list) => {
						if (name) {
							return list.name === name;
						} else if (type) {
							return list.type === type;
						}

						return true;
					});
				}

				if (list && detail) {
					list = await self.getProductDetails(list);
				}

				await dispatch(receiveList(list));

				return list;
			},

			/**
			 * Set the visibility of a product list, making it readable
			 * by other shoppers when public.
			 * The items get the same visibility server side (product_list afterPATCH hook),
			 * since the public list API only returns the public items.
			 * Only the visibility is merged into the stored list,
			 * so the product details already fetched for its items are kept.
			 * @param {string} listId
			 * @param {boolean} isPublic
			 * @returns {Promise<CustomerProductListState>} updated customer product list
			 */
			setListPublic: async (listId: string, isPublic: boolean): Promise<CustomerProductListState> => {
				const updatedList = await updateListVisibility(listId, isPublic);
				await dispatch(setListVisibilityAction({listId, isPublic}));

				return updatedList;
			},

			/**
			 * Whether a product list is public, i.e. readable by other shoppers.
			 * @param {string} listId
			 * @returns {boolean} true if the list is public
			 */
			isListPublic(listId: string): boolean {
				return !!self.findListById(listId)?.public;
			},

			/**
			 * Adds an item to the customer's product list.
			 * @param {string} listId
			 * @param {ICustomerProductListItemInput} item item to be added to the list.
			 */
			createListItem: async (
				listId: string,
				item: ICustomerProductListItemInput,
				dataLayerObject: IWishlistDataLayerOptions = {},
			): Promise<CustomerProductListItem> => {
				const createdItem = await createListItem(listId, item, self.isListPublic(listId));
				const product = managerRepository.getProductsMgr().getProduct(item.id) as ProductModel;
				let sourceCategory = null;
				const productCategory = categoryActions.getCategory(product.getCategoryId());
				const productCategoryTree = productCategory.getParentCategoryTree();

				if (dataLayerObject.sourceSpecification) {
					sourceCategory = categoryActions.getCategory(dataLayerObject.sourceSpecification);
				}

				if (!dataLayerObject.isNotPushDataLayerEvent) {
					if (dataLayerObject.productClickEventForWishlist) {
						const eventObject = {
							eventSourceCategoryId: sourceCategory?.getId() || productCategory?.getId(),
							eventSourceCategoryName: sourceCategory?.getName() || productCategory?.getName(),
							productData: {
								product,
								source: dataLayerObject.source,
								eventSourceCategoryId: sourceCategory?.getId() || productCategory?.getId(),
								eventSourceCategoryName: sourceCategory?.getName() || productCategory?.getName(),
								productCategoriesIds: productCategoryTree?.map((cat) => cat.getId()),
								position: dataLayerObject.position || 0,
								attributes: {
									dimension9: product.getSeason(),
									dimension10: product.getPriceModel().isSalesPriceBookApplied() ? 'MD' : 'FP',
								},
							},
						};

						managerRepository.getEventsMgr().emit(EEventType.PRODUCT_CLICK, eventObject);
					} else {
						const source = dataLayerObject.source ? {source: dataLayerObject.source} : {};
						const eventObject = {
							eventSourceCategoryId: sourceCategory?.getId() || productCategory?.getId(),
							eventSourceCategoryName: sourceCategory?.getName() || productCategory?.getName(),
							...source,
							productData: {
								product,
								eventSourceCategoryId: sourceCategory?.getId() || productCategory?.getId(),
								eventSourceCategoryName: sourceCategory?.getName() || productCategory?.getName(),
								productCategoriesIds: productCategoryTree?.map((cat) => cat.getId()),
								position: dataLayerObject.position || 0,
								...source,
							},
						};

						managerRepository.getEventsMgr().emit(EEventType.PRODUCT_ADD_TO_WISHLIST, eventObject);
					}
				}

				await dispatch(createListItemAction({listId, item: createdItem}));

				return createdItem;
			},

			/**
			 * Update an item in a customer product list
			 *
			 * @param {string} listId id of the list to update the item in
			 * @param {TCustomerProductListItemUpdate} item
			 * @param {string} item.id the id of the item in the product list
			 * @param {number} item.quantity the quantity of the item
			 */
			updateListItem: async (listId: string, item: TCustomerProductListItemUpdate): Promise<CustomerProductListItem | undefined> => {
				const {id, quantity} = item;

				if (quantity === 0) {
					await removeListItem(listId, id);
					await dispatch(removeListItemAction({listId, itemId: id}));

					return;
				}

				const updatedItem = await updateListItem(listId, item, self.isListPublic(listId));
				await dispatch(updateListItemAction({listId, item: updatedItem}));

				return updatedItem;
			},

			/**
			 * Remove an item from a customer product list
			 *
			 * @param {string} listId id of the list to update the item in
			 * @param {string} itemId the id of the item in the product list
			 */
			removeListItem: async (listId: string, itemId: string): Promise<void> => {
				await removeListItem(listId, itemId);
				await dispatch(removeListItemAction({listId, itemId}));
			},

			/**
			 * Remove an item from a customer product list
			 *
			 * @param {string} listId id of the list to update the item in
			 * @param {string} productId the id of the product
			 * @returns {Promise<void> | undefined} remove item from list, undefined when the product is not in the list
			 */
			removeListItemByProductId(listId: string, productId: string): Promise<void> | undefined {
				const item = self.findItemByProductId(listId, productId);

				if (!item) {
					console.warn(`Cannot remove item because product ${productId} is not in the list.`);

					return;
				}

				return self.removeListItem(listId, item.id);
			},

			/**
			 * Remove items from a customer product list retrieving them by variation group
			 * @param {string} listId id of the list to update
			 * @param {string[]} variationGroupIds Variation groups to remove from list
			 * @returns {Promise<void>} remove items from list
			 */
			removeListItemsByVariationGroupIds: async (listId: string, variationGroupIds: string[]): Promise<void> => {
				const items = [];
				variationGroupIds.forEach((id) => {
					const itemsInList = self.findItemsByVariationGroupId(listId, id);

					if (itemsInList) {
						items.push(...itemsInList);
					}
				});

				if (items.length < 1) {
					const err = `Cannot remove items because no products ${variationGroupIds.map((id) => id)} are in the list.`;
					console.warn(err);

					return Promise.reject(err);
				}

				try {
					for (const item of items) {
						await self.removeListItem(listId, item.id);
					}
				} catch (err) {
					return Promise.reject(err);
				}

				return Promise.resolve();
			},

			/**
			 * Get all item details for a product list.
			 * @param {CustomerProductListState} list product list
			 * @param {CustomerProductListItem[]} list.customerProductListItems items array
			 * @returns {Promise<CustomerProductListState>} customer product list
			 */
			getProductDetails: handleAsyncError(async (list: CustomerProductListState): Promise<CustomerProductListState> => {
				// Warning, there is a weird API behavior
				// where if the product list is newly created
				// the "customerProductListItems" key will be missing
				// from the response, so we need to guard it.
				if (!list.customerProductListItems) {
					return {...list, hasDetail: true};
				}

				const ids = list.customerProductListItems.map((item) => item.productId);
				const productDetails = await api.shopperProducts.getProducts({
					parameters: {
						ids: ids.join(','),
						allImages: true,
					},
				});
				const result = self.mergeProductDetailsIntoList(list, productDetails);

				// hasDetail is a flag to indicate
				// the list items are populated
				// with product detail data
				result.hasDetail = true;

				return {...result, hasDetail: true};
			}),

			/**
			 * The customer product list API does NOT return the
			 * product item details, this utility function is used
			 * when you fetch the item details manually and insert
			 * into the lists.
			 * This function is often used with getProductDetails.
			 */
			mergeProductDetailsIntoList(
				list: CustomerProductListState,
				productDetails: ShopperProductsTypes.ProductResult,
			): CustomerProductListState {
				const items = list.customerProductListItems?.map((item) => {
					// Empty object when the product is not returned by the API
					const product = {
						...productDetails.data.find((product) => product.id === item.productId),
					} as ShopperProductsTypes.Product;

					return {
						...item,
						product,
					};
				});

				return {
					...list,
					customerProductListItems: items,
				};
			},

			/**
			 * Find the product list by its name.
			 * @param {string} name
			 * @returns {CustomerProductListState | undefined} customer product list
			 */
			findListByName(name: string): CustomerProductListState | undefined {
				if (!customerProductLists) {
					return undefined;
				}

				return Object.values(customerProductLists).find((list) => list.name === name);
			},

			/**
			 * Get the first product list found.
			 * @param {string|null} type
			 * @returns {CustomerProductListState | undefined} customer product list
			 */
			getFirstList(type?: string | null): CustomerProductListState | undefined {
				if (!customerProductLists) {
					return undefined;
				}

				return Object.values(customerProductLists).find((list) => (type ? list.type === type : true));
			},

			/**
			 * Find the product list by its id.
			 * @param {string} id
			 * @returns {CustomerProductListState | undefined} customer product list
			 */
			findListById(id: string): CustomerProductListState | undefined {
				if (!customerProductLists) {
					return undefined;
				}

				return Object.values(customerProductLists).find((list) => list.id === id);
			},

			/**
			 * Find the item from list.
			 * @param {string} listId
			 * @param {string} productId
			 * @returns {CustomerProductListItem | undefined} product list item
			 */
			findItemByProductId(listId: string, productId: string): CustomerProductListItem | undefined {
				return self.findListById(listId)?.customerProductListItems?.find((item) => item.productId === productId);
			},

			/**
			 * Find the items from list by variation group.
			 * @param {string} listId
			 * @param {string} variationGroupId
			 * @returns {CustomerProductListItem[]} array of product list item
			 */
			findItemsByVariationGroupId(listId: string, variationGroupId: string): CustomerProductListItem[] {
				return (
					self.findListById(listId)?.customerProductListItems?.filter((item) => {
						if (item.c_variationGroupId) {
							return item.c_variationGroupId === variationGroupId;
						} else {
							const product = managerRepository.getProductsMgr().getProduct(item.productId) as ProductModel;

							return product.getVariationGroupId() === variationGroupId;
						}
					}) || []
				);
			},
		};
	}, [customer.customerId, customerProductLists, customer.siteId]);

	return self;
}
